import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Route, Router, Switch, matchPath } from 'react-router-dom';
import { createStore, applyMiddleware, compose } from 'redux';
import rootReducer from './reducers/reducers';
import createSagaMiddleware from 'redux-saga';
import { rootSaga } from './sagas/sagas';
import reduxWebsocket from '@giantmachines/redux-websocket';
import '../css/index.less';
import * as Sentry from '@sentry/react';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import { createBrowserHistory } from 'history';
import { config } from './utils/ga';
import { Environment } from './types';
import routes from './routes';
import { allRoutes } from './helpers/routeHelpers';
import ConfigProvider from 'antd/lib/config-provider';
import * as LogRocket from 'logrocket';
import { AMPLITUDE_API_KEY_DEV, AMPLITUDE_API_KEY_PROD, GA_MEASUREMENT_ID } from './constants';
import * as amplitude from '@amplitude/analytics-browser';
import Spin from 'antd/lib/spin';
import { LoadingOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from './components/styles/globalStyles';
import * as HubspotUtils from './utils/hubspot';
import MainAppContainer from './components/MainAppContainer';
import { isDevelopmentEnvironment } from './helpers';
import userflow from 'userflow.js';
import { StyleProvider } from '@ant-design/cssinjs';
import { initializeAmplitudeExperimentClient } from './utils/internalFeatureFlags';
import { PublicAppContainer } from './components/public/PublicAppContainer';
import * as Logger from '@/utils/logger';

const environment = process.env.NODE_ENV as Environment;
const isDevEnvironment = isDevelopmentEnvironment();

const history = createBrowserHistory();

const appRoutes = allRoutes(routes);

// Logrocket

if (!isDevEnvironment) {
    LogRocket.init('wbldyd/runway', {
        mergeIframes: true,
        rootHostname: 'runway.team',
    });
}

// Sentry

Sentry.init({
    environment: environment,
    dsn: 'https://7effbdf3a8e14892bf3e33d34ca6e553@o461692.ingest.sentry.io/5463811',
    integrations: [
        new TracingIntegrations.BrowserTracing({
            routingInstrumentation: Sentry.reactRouterV5Instrumentation(history, appRoutes, matchPath),
        }),
    ],
    beforeSend(event) {
        // the original error, if needed.
        // const error = hint?.originalException;

        // Filter by message
        // if (event.message?.includes('SomeErrorFromLib')) {
        //     return null;
        // }

        // Filter by stack trace filename
        const stack = event.exception?.values?.[0]?.stacktrace?.frames || [];
        const isLinkedInInsights = stack.some((frame) => frame.filename?.includes('li.lms-analytics'));
        const isReactDom = stack.some((frame) => frame.filename?.includes('react-dom.production'));
        const isHubspotAnalytics = stack.some((frame) => frame.filename?.includes('js.hs-analytics.net'));

        if (isLinkedInInsights || isReactDom || isHubspotAnalytics) {
            Logger.log(`Filtered out possible Sentry event based on file name: ${JSON.stringify(event)}`);
            return null;
        }

        return event;
    },
    release: SENTRY_RELEASE,
    tracesSampleRate: 0.2,
});

// Amplitude
const apiKey = isDevEnvironment ? AMPLITUDE_API_KEY_DEV : AMPLITUDE_API_KEY_PROD;

amplitude.init(apiKey, {
    appVersion: PACKAGE_VERSION,
    defaultTracking: {
        attribution: true,
        pageViews: false,
        sessions: false,
        formInteractions: false,
        fileDownloads: false,
    },
    // logLevel: amplitude.Types.LogLevel.Debug, //uncomment for debug amplitude logging
});

// Amplitude Experiments / Feature flags - note, do not move, must be initialized AFTER the main Amplitude analytics SDK

initializeAmplitudeExperimentClient(isDevEnvironment);

// Middlewares

const middlewares = [];

const sagaMiddleware = createSagaMiddleware();
const reduxWebsocketMiddleware = reduxWebsocket({ reconnectOnClose: false, reconnectOnError: false });

middlewares.push(sagaMiddleware);
middlewares.push(reduxWebsocketMiddleware);
middlewares.push(LogRocket.reduxMiddleware());

// expose Logrocket to the window object (required for integration into userflow)
window.LogRocket = LogRocket;

// Create Redux Store

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const store = createStore(rootReducer, composeEnhancers(applyMiddleware(...middlewares)));

sagaMiddleware.run(rootSaga);

const additionalGaParams = isDevEnvironment ? { debug_mode: true } : {};

// Update tracker paths upon history location changes

history.listen((location) => {
    // GA
    config(GA_MEASUREMENT_ID, {
        page_path: location.pathname,
        send_page_view: false,
        ...additionalGaParams,
    });

    // Hubspot Tracking
    HubspotUtils.updatePath(location.pathname);
});

Spin.setDefaultIndicator(<LoadingOutlined spin style={{ fontSize: 30, color: runwayColors.info }} />);

// force light mode for MDEditor
document.documentElement.setAttribute('data-color-mode', 'light');

userflow.init(USERFLOW_TOKEN);

ReactDOM.render(
    <React.StrictMode>
        <Provider store={store}>
            <Router history={history}>
                <StyleProvider hashPriority="high">
                    <ConfigProvider
                        form={{ validateMessages: { required: '${label} is required' } }}
                        modal={{
                            styles: {
                                content: {
                                    backgroundColor: antGrey.grey3,
                                    padding: 0,
                                },
                                body: {
                                    padding: 24,
                                },
                                header: {
                                    padding: '16px 24px',
                                    backgroundColor: runwayColors.white,
                                    marginBottom: 0,
                                },
                                footer: {
                                    padding: '16px 24px',
                                    borderRadius: 10,
                                    backgroundColor: runwayColors.white,
                                },
                            },
                        }}
                        theme={{
                            token: {
                                colorPrimary: runwayColors.primary,
                                colorHighlight: runwayColors.statusRed,
                                colorInfo: runwayColors.info,
                                colorSuccess: runwayColors.statusGreen,
                                colorWarning: runwayColors.statusYellow,
                                colorError: runwayColors.statusRed,
                                borderRadius: 8,
                                borderRadiusSM: 4,
                                colorBgLayout: runwayColors.white,
                                colorLink: runwayColors.info,
                                colorBgBase: runwayColors.white,
                                colorBgContainer: runwayColors.white,
                                colorPrimaryActive: runwayColors.primary,
                                fontFamily: 'Inter, -apple-system, sans-serif',
                            },
                        }}
                    >
                        <Switch>
                            <Route path={[routes.publicBucket]} component={PublicAppContainer} />
                            <MainAppContainer environment={environment} history={history} />
                        </Switch>
                    </ConfigProvider>
                </StyleProvider>
            </Router>
        </Provider>
    </React.StrictMode>,
    document.getElementById('main'),
);
